import { TActivityFeedItemResponse } from '@daymade/api-contracts';
import {
  action,
  makeAutoObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
} from 'mobx';
import { ActivityFeedItem } from 'types/ActivityFeedItem';
import { activityFeedApiClient } from 'utils/apiClient';
import {
  ACTIVTIY_FEED_FETCH_INTERVAL,
  MINIMUM_EVENT_AGE,
} from 'utils/dashboard/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { RootStore } from './RootStore';

export class ActivityFeedStore {
  root: RootStore;
  displayedItems: ActivityFeedItem[] = [];
  queue: ActivityFeedItem[] = [];
  interval: NodeJS.Timeout | null = null;
  lastEventId: number | null = null;
  isFetching: boolean = false;

  constructor(root: RootStore) {
    this.root = root;

    makeAutoObservable(this, {
      displayedItems: observable,
      stopFetching: action,
      startFetching: action,
    });

    onBecomeObserved(this, 'displayedItems', this.startFetching);
    onBecomeUnobserved(this, 'displayedItems', this.stopFetching);
  }

  /** Starts data fetching and updates */
  startFetching = async () => {
    if (this.interval) {
      console.log('Already started fetching activity feed items');
      return;
    }
    console.log('ActivityFeedStore: Starting data fetching');
    await this.fetchInitialItems();
    await this.fetchNextItems();
    this.startQueueHandler();
    this.interval = setInterval(
      this.fetchNextItems,
      ACTIVTIY_FEED_FETCH_INTERVAL
    );
  };

  /** Stops data fetching and clears data */
  stopFetching = () => {
    console.log('ActivityFeedStore: Stopping data fetching');
    this.setDisplayedItems([]);
    this.setLastEventId(null);
    this.isFetching = false;
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  };

  startQueueHandler = () => {
    reaction(
      () => ({
        queue: this.queue,
      }),
      async ({ queue }) => {
        this.onChangeQueue(queue);
      }
    );
  };

  /** Fetches the initial 4 items */
  fetchInitialItems = async () => {
    if (this.isFetching) return;
    this.isFetching = true;

    const { status, body: activityFeedItems } =
      await activityFeedApiClient.public.getActivityFeedItems();

    if (status === HttpStatusCode.OK) {
      const parsedBody = this.transformActivityFeedItems(activityFeedItems);
      this.setDisplayedItems(parsedBody);

      if (activityFeedItems.length > 0) {
        this.setLastEventId(activityFeedItems[0].id);
      }
    }

    this.isFetching = false;
  };

  fetchNextItems = async () => {
    if (this.isFetching || !this.lastEventId) return;

    this.isFetching = true;

    const { status, body: activityFeedItems } =
      await activityFeedApiClient.public.getActivityFeedItems({
        query: { lastEventId: this.lastEventId },
      });

    if (status === HttpStatusCode.OK) {
      const parsedBody = this.transformActivityFeedItems(activityFeedItems);
      this.queue = parsedBody;

      if (activityFeedItems.length > 0) {
        this.setLastEventId(activityFeedItems[0].id);
      }
    }

    this.isFetching = false;
  };

  onChangeQueue = (queue: ActivityFeedItem[]) => {
    if (queue.length === 0) {
      return;
    }
    for (let i = 0; i < queue.length; i++) {
      const eventTimestamp = new Date(queue[i].timestamp);
      const now = new Date();
      const eventAgeMs = now.getTime() - eventTimestamp.getTime();

      if (eventAgeMs < MINIMUM_EVENT_AGE) {
        const remainingTimeMs = MINIMUM_EVENT_AGE - eventAgeMs;
        setTimeout(() => this.updateDisplayedItems(queue[i]), remainingTimeMs);
      } else {
        this.updateDisplayedItems(queue[i]);
      }

      if (i === 0) {
        // SET to latest timestamp in the queue
        this.setLastEventId(this.queue[i].id);
      }
    }
    this.queue = [];
  };

  updateDisplayedItems = async (activityItem: ActivityFeedItem) => {
    let newItems: ActivityFeedItem[];

    if (this.displayedItems.length >= 4) {
      // Already at (or above) 4 items: keep only the first 3 old ones, drop the rest
      newItems = [activityItem, ...this.displayedItems.slice(0, 3)];
    } else {
      // Fewer than 4 items: just prepend, don’t slice any out
      newItems = [activityItem, ...this.displayedItems];
    }

    this.setDisplayedItems(newItems);
    console.log('updating display', this.interval);
  };

  setDisplayedItems = (items: ActivityFeedItem[]) => {
    this.displayedItems = items;
  };

  setLastEventId = (lastEventId: number | null) => {
    this.lastEventId = lastEventId;
  };

  private transformActivityFeedItems(items: TActivityFeedItemResponse) {
    return items.map((item) => {
      const { placeholderData, ...rest } = item;
      return JSON.parse(
        JSON.stringify({ ...rest, placeholder_data: placeholderData })
      );
    });
  }
}
